import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Check, Mic, MicOff, Loader2, User, Package,
  ShieldCheck, FileSignature, Truck, DollarSign, Rocket, Plus, Trash2, Search,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { binderDataUri } from "@/lib/pdf";

const TRADES = [
  { key: "av_low_voltage", label: "AV / Low-Voltage" },
  { key: "electrical", label: "Electrical" },
  { key: "plumbing", label: "Plumbing" },
  { key: "hvac", label: "HVAC" },
  { key: "general_contractor", label: "General Contractor" },
  { key: "security_alarm", label: "Security & Alarm" },
  { key: "networking_it", label: "Networking / IT" },
  { key: "solar", label: "Solar / Renewables" },
];
const STEPS = [
  { key: "customer", label: "Customer", icon: User },
  { key: "scope", label: "Voice Scope", icon: Mic },
  { key: "materials", label: "Materials & Safety", icon: ShieldCheck },
  { key: "proposal", label: "Proposal", icon: FileSignature },
  { key: "dispatch", label: "Schedule", icon: Truck },
  { key: "closeout", label: "Closeout", icon: DollarSign },
];
const MARGIN_FLOOR = 15;
const DEFAULT_OSHA = ["PPE inspected and worn", "Work area secured / hazards flagged", "Ladders & lifts inspected", "Lockout-tagout applied where required", "First-aid kit on site"];
const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-base text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";

function SigPad({ onChange }) {
  const ref = useRef(null); const drawing = useRef(false); const pts = useRef(0);
  const at = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches?.[0] || e; return [(t.clientX - r.left) * (c.width / r.width), (t.clientY - r.top) * (c.height / r.height)]; };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext("2d"); const [x, y] = at(e); c.beginPath(); c.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); const [x, y] = at(e); c.lineTo(x, y); c.strokeStyle = "#fff"; c.lineWidth = 2.5; c.stroke(); pts.current++; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (pts.current >= 8) onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); pts.current = 0; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={560} height={150} data-testid="sig-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full touch-none rounded-lg border border-slate-700 bg-[#0d1420]" />
      <button type="button" onClick={clear} className="mt-1 text-xs text-slate-500 hover:text-white">Clear signature</button>
    </div>
  );
}

export default function NewJobWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  // shared job state
  const [job, setJob] = useState({ id: null, trade: "av_low_voltage", customer: null });
  const [scope, setScope] = useState({ transcript: "", extracted: null, confirmed: false });
  const [lines, setLines] = useState([]);
  const [pretask, setPretask] = useState({ items: [], checked: {}, crewLead: "", sig: null, locked: false });
  const [proposal, setProposal] = useState({ estimateId: null, taxRate: 8.25, signerName: "", sig: null, signed: false, override: false });
  const [dispatch, setDispatch] = useState({ data: null, primary: "", helper: "", vehicle: "", assigned: false, rolledOut: false, miles: "" });
  const [closeout, setCloseout] = useState({ kind: "deposit", amount: "", online: true, paid: false, done: false });

  const gates = [!!job.id, scope.confirmed, pretask.locked, proposal.signed, dispatch.assigned, closeout.done];
  const canNext = gates[step];
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0B0F17]" data-testid="new-job-wizard">
      {/* header + progress */}
      <div className="border-b border-slate-800 px-4 pt-4 pb-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button onClick={() => nav("/dispatch")} data-testid="wizard-exit" className="rounded-lg px-3 py-2 text-xs text-slate-500 hover:text-white">Exit</button>
          <span className="font-mono-tac text-[11px] uppercase tracking-widest text-amber-400">New Job · Step {step + 1}/6</span>
          <span className="w-8" />
        </div>
        <div className="mx-auto mt-3 flex max-w-2xl items-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-emerald-500" : i === step ? "bg-amber-500" : "bg-slate-800"}`} />
          ))}
        </div>
        <div className="mx-auto mt-2 flex max-w-2xl items-center gap-2 text-white">
          {(() => { const I = STEPS[step].icon; return <I className="h-5 w-5 text-amber-400" />; })()}
          <h1 className="font-display text-xl font-bold">{STEPS[step].label}</h1>
        </div>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mx-auto max-w-2xl">
          {step === 0 && <StepCustomer {...{ job, setJob, next, busy, setBusy }} />}
          {step === 1 && <StepScope {...{ job, scope, setScope, busy, setBusy }} />}
          {step === 2 && <StepMaterials {...{ job, scope, lines, setLines, pretask, setPretask, busy, setBusy }} />}
          {step === 3 && <StepProposal {...{ job, lines, proposal, setProposal, busy, setBusy }} />}
          {step === 4 && <StepDispatch {...{ job, dispatch, setDispatch, busy, setBusy }} />}
          {step === 5 && <StepCloseout {...{ job, closeout, setCloseout, busy, setBusy, nav }} />}
        </div>
      </div>

      {/* footer gates */}
      <div className="border-t border-slate-800 px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button onClick={back} disabled={step === 0} data-testid="wizard-back"
            className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={!canNext} data-testid="wizard-next"
              className="btn-amber inline-flex flex-1 items-center justify-center gap-1 rounded-full px-6 py-2.5 text-sm disabled:opacity-40">
              {canNext ? "Next" : "Complete this step to continue"} <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => nav("/dispatch")} disabled={!closeout.done} data-testid="wizard-finish"
              className="btn-amber inline-flex flex-1 items-center justify-center gap-1 rounded-full px-6 py-2.5 text-sm disabled:opacity-40">
              <Check className="h-4 w-4" /> Return to dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 1: Customer & Job ---------- */
function StepCustomer({ job, setJob, next, busy, setBusy }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ name: "", email: "", phone: "" });
  const [prop, setProp] = useState({ label: "Main Site", address: "" });
  const [title, setTitle] = useState("");

  useEffect(() => { api.get("/customers").then(({ data }) => setList(data || [])).catch(() => setList([])); }, []);
  const filtered = list.filter((c) => c.name?.toLowerCase().includes(q.toLowerCase()));

  const createCustomer = async () => {
    if (!nc.name.trim()) return toast.error("Customer name required");
    setBusy(true);
    try {
      const { data } = await api.post("/customers", nc);
      setList((l) => [data, ...l]); setJob((j) => ({ ...j, customer: data })); setCreating(false); setNc({ name: "", email: "", phone: "" });
      toast.success("Customer created");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const createJob = async () => {
    if (!job.customer) return toast.error("Select a customer first");
    setBusy(true);
    try {
      let propertyId = null;
      if (prop.address.trim()) {
        const { data: p } = await api.post("/properties", { customer_id: job.customer.id, label: prop.label, address: prop.address });
        propertyId = p.id;
      }
      const { data: j } = await api.post("/jobs", {
        title: title.trim() || `${TRADES.find((t) => t.key === job.trade)?.label} — ${job.customer.name}`,
        customer_id: job.customer.id, property_id: propertyId, trade: job.trade,
      });
      setJob((s) => ({ ...s, id: j.id, property_id: propertyId }));
      toast.success("Job created"); next();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (job.id) return <div className="card-tac p-5 text-sm text-emerald-300" data-testid="customer-done"><Check className="mb-1 inline h-4 w-4" /> Job created for <b>{job.customer?.name}</b>. Tap Next.</div>;

  return (
    <div className="space-y-4">
      {!job.customer ? (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
            <input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing customers" className={inp + " pl-10"} />
          </div>
          <div className="space-y-2">
            {filtered.map((c) => (
              <button key={c.id} data-testid={`cust-pick-${c.id}`} onClick={() => setJob((j) => ({ ...j, customer: c }))}
                className="card-tac flex w-full items-center justify-between p-3 text-left hover:border-amber-500">
                <span className="text-sm text-white">{c.name}</span>
                <ChevronRight className="h-4 w-4 text-slate-500" />
              </button>
            ))}
            {q && filtered.length === 0 && <p className="text-xs text-slate-500">No match. Create a new customer below.</p>}
          </div>
          {!creating ? (
            <button data-testid="cust-new-toggle" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-amber-500"><Plus className="h-4 w-4" /> New customer</button>
          ) : (
            <div className="card-tac space-y-2 p-4">
              <input data-testid="cust-name" placeholder="Customer / company name" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} className={inp} />
              <input data-testid="cust-email" placeholder="Email (optional)" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className={inp} />
              <input data-testid="cust-phone" placeholder="Phone +1… (optional)" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className={inp} />
              <button data-testid="cust-create" disabled={busy || !nc.name} onClick={createCustomer} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Create customer</button>
            </div>
          )}
        </>
      ) : (
        <div className="card-tac space-y-3 p-4" data-testid="job-setup">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white">Customer: <b>{job.customer.name}</b></div>
            <button onClick={() => setJob((j) => ({ ...j, customer: null }))} className="text-xs text-slate-500 hover:text-white">Change</button>
          </div>
          <label className="text-xs text-slate-400">Trade</label>
          <select data-testid="job-trade" value={job.trade} onChange={(e) => setJob((j) => ({ ...j, trade: e.target.value }))} className={inp}>
            {TRADES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <input data-testid="job-title" placeholder="Job title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className={inp} />
          <label className="text-xs text-slate-400">Property address (optional)</label>
          <input data-testid="prop-address" placeholder="Service address" value={prop.address} onChange={(e) => setProp({ ...prop, address: e.target.value })} className={inp} />
          <button data-testid="job-create" disabled={busy} onClick={createJob} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">{busy ? "Creating…" : "Create job & continue"}</button>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 2: Voice scope ---------- */
function StepScope({ job, scope, setScope, busy, setBusy }) {
  const [rec, setRec] = useState(false);
  const recRef = useRef(null);
  const toggleRec = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice not supported here — type the scope below");
    if (rec) { recRef.current?.stop(); setRec(false); return; }
    const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = "en-US";
    r.onresult = (e) => { let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; setScope((s) => ({ ...s, transcript: t })); };
    r.onend = () => setRec(false);
    recRef.current = r; r.start(); setRec(true);
  };
  const extract = async () => {
    if (!scope.transcript.trim()) return toast.error("Record or type the scope first");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/voice-debrief", { job_id: job.id, transcript: scope.transcript });
      setScope((s) => ({ ...s, extracted: data.extracted || {}, confirmed: false }));
      toast.success("Scope extracted — review below");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const ex = scope.extracted;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Describe the job out loud — Claude turns your speech into a structured scope. You can also type it.</p>
      <button data-testid="scope-mic" onClick={toggleRec} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm ${rec ? "bg-rose-500 text-white" : "border border-slate-700 text-white hover:border-amber-500"}`}>
        {rec ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />} {rec ? "Stop recording" : "Start voice"}
      </button>
      <textarea data-testid="scope-transcript" rows={5} value={scope.transcript} onChange={(e) => setScope((s) => ({ ...s, transcript: e.target.value }))} placeholder="e.g. Mount a 75 inch TV, run in-wall HDMI and power, add 6 in-ceiling speakers, roughly 8 hours labor…" className={inp} />
      <button data-testid="scope-extract" disabled={busy} onClick={extract} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Extract scope with AI</button>

      {ex && (
        <div className="card-tac space-y-2 p-4" data-testid="scope-review">
          <div className="text-xs uppercase tracking-widest text-slate-500">Extracted scope</div>
          {ex.work_performed && <div className="text-sm text-white">{ex.work_performed}</div>}
          {Array.isArray(ex.parts_used) && ex.parts_used.length > 0 && (
            <ul className="mt-1 space-y-1 text-sm text-slate-300">
              {ex.parts_used.map((p, i) => <li key={i}>• {p.name || p.item} {p.quantity ? `×${p.quantity}` : ""}</li>)}
            </ul>
          )}
          {ex.hours_worked != null && <div className="text-xs text-slate-400">Labor: {ex.hours_worked} hrs</div>}
          <button data-testid="scope-confirm" onClick={() => { setScope((s) => ({ ...s, confirmed: true })); toast.success("Scope confirmed"); }} className={`mt-2 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm ${scope.confirmed ? "bg-emerald-600 text-white" : "btn-amber"}`}>
            <Check className="h-4 w-4" /> {scope.confirmed ? "Scope confirmed" : "Confirm scope"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 3: Materials + OSHA ---------- */
function StepMaterials({ job, scope, lines, setLines, pretask, setPretask, busy, setBusy }) {
  useEffect(() => {
    if (lines.length === 0 && Array.isArray(scope.extracted?.parts_used)) {
      setLines(scope.extracted.parts_used.map((p) => ({ item: p.name || p.item || "", category: "parts", qty: Number(p.quantity) || 1, unit_cost: 0, unit_price: 0 })));
    }
  }, []); // eslint-disable-line
  useEffect(() => {
    api.get(`/pretask-template?trade=${job.trade}`)
      .then(({ data }) => setPretask((s) => ({ ...s, items: (data.items && data.items.length) ? data.items : DEFAULT_OSHA })))
      .catch(() => setPretask((s) => ({ ...s, items: DEFAULT_OSHA })));
  }, [job.trade, setPretask]);

  const setLine = (i, k, v) => setLines((L) => L.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addLine = () => setLines((L) => [...L, { item: "", category: "parts", qty: 1, unit_cost: 0, unit_price: 0 }]);
  const rmLine = (i) => setLines((L) => L.filter((_, j) => j !== i));
  const allChecked = pretask.items.length > 0 && pretask.items.every((it) => pretask.checked[it]);

  const lock = async () => {
    if (!allChecked) return toast.error("Acknowledge every safety item");
    if (!pretask.crewLead.trim()) return toast.error("Crew lead name required");
    if (!pretask.sig) return toast.error("Crew lead signature required");
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/pretask`, { items: pretask.checked, crew_lead: pretask.crewLead, signature: pretask.sig });
      api.patch(`/jobs/${job.id}/status`, { status: "in_progress" }).catch(() => {});
      setPretask((s) => ({ ...s, locked: true })); toast.success("OSHA pre-task locked — job can start");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 text-sm font-semibold text-white">Materials, labor & stock</div>
        <p className="mb-2 text-xs text-slate-400">Categorize each line: <b>parts</b> (materials), <b>hours</b> (labor), <b>stock</b> (pulled from a truck) or <b>equipment</b>. Cost is what you pay; price is what you charge.</p>
        <div className="space-y-2">
          {lines.map((r, i) => (
            <div key={i} data-testid={`mat-row-${i}`} className="card-tac grid grid-cols-2 gap-2 p-2 sm:grid-cols-6">
              <input data-testid={`mat-item-${i}`} placeholder="Description" value={r.item} onChange={(e) => setLine(i, "item", e.target.value)} className={inp + " col-span-2 py-2 text-sm sm:col-span-2"} />
              <select data-testid={`mat-cat-${i}`} value={r.category} onChange={(e) => setLine(i, "category", e.target.value)} className={inp + " py-2 text-sm"}>
                <option value="parts">Parts</option><option value="hours">Hours</option><option value="stock">Stock</option><option value="equipment">Equipment</option>
              </select>
              <input data-testid={`mat-qty-${i}`} type="number" placeholder="Qty" value={r.qty} onChange={(e) => setLine(i, "qty", Number(e.target.value))} className={inp + " py-2 text-sm"} />
              <input data-testid={`mat-cost-${i}`} type="number" placeholder="Cost" value={r.unit_cost} onChange={(e) => setLine(i, "unit_cost", Number(e.target.value))} className={inp + " py-2 text-sm"} />
              <div className="flex items-center gap-1">
                <input data-testid={`mat-price-${i}`} type="number" placeholder="Price" value={r.unit_price} onChange={(e) => setLine(i, "unit_price", Number(e.target.value))} className={inp + " py-2 text-sm"} />
                <button onClick={() => rmLine(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="mat-add" onClick={addLine} className="mt-2 inline-flex items-center gap-1 rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-amber-500"><Plus className="h-3.5 w-3.5" /> Add line</button>
      </div>

      <div className="card-tac p-4" data-testid="osha-gate">
        <div className="mb-2 flex items-center gap-2 text-white"><ShieldCheck className="h-4 w-4 text-amber-400" /><span className="text-sm font-semibold">OSHA pre-task gate</span></div>
        {pretask.locked ? (
          <div className="text-sm text-emerald-300"><Check className="mb-0.5 mr-1 inline h-4 w-4" /> Locked by {pretask.crewLead}. Job cleared to proceed.</div>
        ) : (
          <>
            <div className="space-y-1.5">
              {pretask.items.map((it) => (
                <label key={it} className="flex items-start gap-2 text-sm text-slate-300">
                  <input type="checkbox" data-testid={`osha-${it.slice(0, 12)}`} checked={!!pretask.checked[it]} onChange={(e) => setPretask((s) => ({ ...s, checked: { ...s.checked, [it]: e.target.checked } }))} className="mt-1" />
                  <span>{it}</span>
                </label>
              ))}
            </div>
            <input data-testid="osha-crewlead" placeholder="Crew lead name" value={pretask.crewLead} onChange={(e) => setPretask((s) => ({ ...s, crewLead: e.target.value }))} className={inp + " mt-3"} />
            <div className="mt-2 text-xs text-slate-400">Crew lead signature</div>
            <SigPad onChange={(d) => setPretask((s) => ({ ...s, sig: d }))} />
            <button data-testid="osha-lock" disabled={busy} onClick={lock} className="btn-amber mt-2 w-full rounded-xl py-3 text-sm disabled:opacity-50">{busy ? "Locking…" : "Sign & lock pre-task"}</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Step 4: Proposal + signature ---------- */
function StepProposal({ job, lines, proposal, setProposal, busy, setBusy }) {
  const revenue = lines.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);
  const cost = lines.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.unit_cost) || 0), 0);
  const tax = revenue * (Number(proposal.taxRate) || 0) / 100;
  const total = revenue + tax;
  const margin = revenue > 0 ? Math.round((revenue - cost) / revenue * 100) : 0;
  const belowFloor = revenue > 0 && margin < MARGIN_FLOOR;

  const buildAndSign = async () => {
    if (revenue <= 0) return toast.error("Add priced line items in Step 3 first");
    if (belowFloor && !proposal.override) return toast.error(`Margin ${margin}% is below the ${MARGIN_FLOOR}% floor — enable override to proceed`);
    if (!proposal.signerName.trim()) return toast.error("Signer name required");
    if (!proposal.sig) return toast.error("Customer signature required");
    setBusy(true);
    try {
      let estId = proposal.estimateId;
      if (!estId) {
        const { data: est } = await api.post("/estimates", { job_id: job.id, line_items: lines.map((r) => ({ item: r.item, qty: r.qty, unit_price: r.unit_price, unit_cost: r.unit_cost, category: r.category })) });
        estId = est.id; setProposal((p) => ({ ...p, estimateId: estId }));
      }
      let gps = {};
      try { const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })); gps = { lat: pos.coords.latitude, lng: pos.coords.longitude }; } catch { /* optional */ }
      await api.post("/estimates/sign", { estimate_id: estId, signer_name: proposal.signerName, signature_image: proposal.sig, consent_text: "I approve this estimate and authorize the work as scoped.", device: navigator.userAgent, ...gps });
      setProposal((p) => ({ ...p, signed: true })); toast.success("Proposal signed");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="card-tac p-4">
        <div className="mb-2 text-xs uppercase tracking-widest text-slate-500">Proposal summary</div>
        {lines.length === 0 ? <p className="text-sm text-slate-500">No line items — go back to Step 3.</p> : lines.map((r, i) => (
          <div key={i} className="flex justify-between py-0.5 text-sm text-slate-300"><span>{r.item || "(item)"} ×{r.qty}</span><span>${((Number(r.qty) || 0) * (Number(r.unit_price) || 0)).toFixed(2)}</span></div>
        ))}
        <div className="mt-2 flex items-center gap-2 border-t border-slate-800 pt-2 text-sm">
          <span className="text-slate-400">Tax %</span>
          <input data-testid="prop-tax" type="number" value={proposal.taxRate} onChange={(e) => setProposal((p) => ({ ...p, taxRate: Number(e.target.value) }))} className={inp + " w-24 py-1"} />
        </div>
        <div className="mt-2 space-y-0.5 text-sm">
          <div className="flex justify-between text-slate-400"><span>Subtotal</span><span>${revenue.toFixed(2)}</span></div>
          <div className="flex justify-between text-slate-400"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-semibold text-white"><span>Total</span><span data-testid="prop-total">${total.toFixed(2)}</span></div>
          <div className={`flex justify-between ${belowFloor ? "text-rose-400" : "text-emerald-400"}`} data-testid="prop-margin"><span>Margin</span><span>{margin}%</span></div>
        </div>
        {belowFloor && (
          <label className="mt-2 flex items-center gap-2 rounded-lg border border-rose-600/40 bg-rose-500/10 p-2 text-xs text-rose-300">
            <input type="checkbox" data-testid="prop-override" checked={proposal.override} onChange={(e) => setProposal((p) => ({ ...p, override: e.target.checked }))} />
            Margin below {MARGIN_FLOOR}% floor — override to allow this proposal
          </label>
        )}
      </div>
      {proposal.signed ? (
        <div className="card-tac p-4 text-sm text-emerald-300" data-testid="prop-signed"><Check className="mb-0.5 mr-1 inline h-4 w-4" /> Signed by {proposal.signerName}. Tap Next to schedule.</div>
      ) : (
        <div className="card-tac space-y-2 p-4">
          <input data-testid="prop-signer" placeholder="Customer signer name" value={proposal.signerName} onChange={(e) => setProposal((p) => ({ ...p, signerName: e.target.value }))} className={inp} />
          <div className="text-xs text-slate-400">Customer signature</div>
          <SigPad onChange={(d) => setProposal((p) => ({ ...p, sig: d }))} />
          <button data-testid="prop-sign" disabled={busy} onClick={buildAndSign} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">{busy ? "Saving…" : "Sign & accept proposal"}</button>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 5: Schedule / Dispatch / En route ---------- */
function StepDispatch({ job, dispatch, setDispatch, busy, setBusy }) {
  useEffect(() => { api.get(`/jobs/${job.id}/assignment`).then(({ data }) => setDispatch((s) => ({ ...s, data }))).catch(() => {}); }, [job.id, setDispatch]);
  const d = dispatch.data || {};
  const techs = d.suggested || d.techs || d.candidates || d.members || [];
  const vehicles = d.vehicles || d.available_vehicles || [];

  const assign = async () => {
    if (!dispatch.primary) return toast.error("Pick a lead technician");
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/assign`, { primary_id: dispatch.primary, helper_id: dispatch.helper || null, vehicle_id: dispatch.vehicle || null });
      setDispatch((s) => ({ ...s, assigned: true })); toast.success("Assigned — tech notified by SMS");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const rollout = async () => {
    setBusy(true);
    try {
      let gps = {};
      try { const p = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })); gps = { lat: p.coords.latitude, lng: p.coords.longitude }; } catch { /* optional */ }
      await api.post(`/jobs/${job.id}/rollout`, gps);
      if (dispatch.miles) await api.post(`/jobs/${job.id}/mileage`, { miles: Number(dispatch.miles) });
      setDispatch((s) => ({ ...s, rolledOut: true })); toast.success("En route — live margin tracking on");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      {d.heavy_assets?.length > 0 && <div className="rounded-lg border border-amber-600/40 bg-amber-500/10 p-3 text-xs text-amber-300">Heavy assets flagged: {d.heavy_assets.join(", ")} — assign qualified crew + truck.</div>}
      <div className="card-tac space-y-2 p-4">
        <label className="text-xs text-slate-400">Lead technician</label>
        <select data-testid="disp-primary" value={dispatch.primary} onChange={(e) => setDispatch((s) => ({ ...s, primary: e.target.value }))} className={inp}>
          <option value="">Select…</option>
          {techs.map((t) => <option key={t.id} value={t.id}>{t.name}{t.qualified === false ? " (not certified)" : ""}</option>)}
        </select>
        <label className="text-xs text-slate-400">Helper (optional)</label>
        <select data-testid="disp-helper" value={dispatch.helper} onChange={(e) => setDispatch((s) => ({ ...s, helper: e.target.value }))} className={inp}>
          <option value="">None</option>
          {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <label className="text-xs text-slate-400">Truck (optional)</label>
        <select data-testid="disp-vehicle" value={dispatch.vehicle} onChange={(e) => setDispatch((s) => ({ ...s, vehicle: e.target.value }))} className={inp}>
          <option value="">None</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name || v.plate}</option>)}
        </select>
        <button data-testid="disp-assign" disabled={busy} onClick={assign} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">{dispatch.assigned ? "Reassign" : "Assign & notify tech"}</button>
        {techs.length === 0 && <p className="text-xs text-slate-500">No technicians yet — add crew under Team, then reopen.</p>}
      </div>
      {dispatch.assigned && (
        <div className="card-tac space-y-2 p-4" data-testid="disp-rollout">
          <div className="text-sm font-semibold text-white">Roll-out / En route</div>
          <input data-testid="disp-miles" type="number" placeholder="Est. travel miles (optional)" value={dispatch.miles} onChange={(e) => setDispatch((s) => ({ ...s, miles: e.target.value }))} className={inp} />
          <button data-testid="disp-rollout-btn" disabled={busy} onClick={rollout} className={`w-full rounded-xl py-3 text-sm ${dispatch.rolledOut ? "bg-emerald-600 text-white" : "btn-amber"}`}>{dispatch.rolledOut ? "En route ✓" : "Start roll-out (GPS + fuel)"}</button>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 6: Closeout ---------- */
function StepCloseout({ job, closeout, setCloseout }) {
  const [busy, setBusy] = useState(false);
  const capture = async () => {
    const amt = Number(closeout.amount);
    if (!amt || amt <= 0) return toast.error("Enter a payment amount");
    setBusy(true);
    try {
      await api.post("/billing/field-payment", { job_id: job.id, kind: closeout.kind, amount: amt, online: closeout.online });
      setCloseout((s) => ({ ...s, paid: true })); toast.success(closeout.online ? "Payment captured" : "Payment queued (offline)");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const complete = async () => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/status`, { status: "completed" }).catch(() => {});
      try {
        const { data: b } = await api.get(`/jobs/${job.id}/binder`);
        const base64 = binderDataUri(b).split(",")[1];
        await api.post(`/jobs/${job.id}/binder/send`, { pdf_base64: base64, filename: "job-binder.pdf" });
        toast.success("Immutable binder emailed to customer + office");
      } catch { toast.warning("Job completed, but binder auto-send failed"); }
      setCloseout((s) => ({ ...s, done: true }));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="card-tac space-y-2 p-4">
        <div className="text-sm font-semibold text-white">Final payment</div>
        <select data-testid="close-kind" value={closeout.kind} onChange={(e) => setCloseout((s) => ({ ...s, kind: e.target.value }))} className={inp}>
          <option value="deposit">Deposit</option><option value="progress">Progress</option><option value="full">Full balance</option>
        </select>
        <input data-testid="close-amount" type="number" placeholder="Amount $" value={closeout.amount} onChange={(e) => setCloseout((s) => ({ ...s, amount: e.target.value }))} className={inp} />
        <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" data-testid="close-online" checked={closeout.online} onChange={(e) => setCloseout((s) => ({ ...s, online: e.target.checked }))} /> Collected online (uncheck for offline / cash)</label>
        <button data-testid="close-pay" disabled={busy} onClick={capture} className={`w-full rounded-xl py-3 text-sm ${closeout.paid ? "bg-emerald-600 text-white" : "btn-amber"}`}>{closeout.paid ? "Payment recorded ✓" : "Capture payment"}</button>
      </div>
      <div className="card-tac space-y-2 p-4">
        <div className="text-sm font-semibold text-white">Complete & binder</div>
        <p className="text-xs text-slate-400">Marks the job complete and emails the checksum-locked, immutable job binder PDF to the customer and office.</p>
        <button data-testid="close-complete" disabled={busy} onClick={complete} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">{busy ? "Closing…" : closeout.done ? "Closed out ✓" : "Complete job & send binder"}</button>
        {closeout.done && <div className="text-sm text-emerald-300" data-testid="close-done"><Check className="mb-0.5 mr-1 inline h-4 w-4" /> Job closed out. Tap “Return to dashboard”.</div>}
      </div>
    </div>
  );
}
